import api from './api'
import type { ApiResponse, PaginatedResponse, PaginationMeta } from '@/types/api.types'
import type {
  Invoice,
  InvoiceAttachment,
  InvoiceFilters,
  InvoicePayment,
  InvoicePaymentFile,
} from '@/types/selling.types'

function mapListResponse<T>(r: { data: T[]; total?: number; page: number; per_page: number; total_pages?: number }) {
  return { 
    data: r.data, 
    meta: { 
      current_page: r.page, 
      per_page: r.per_page, 
      total: r.total || r.data.length, 
      last_page: r.total_pages || 1 
    } as PaginationMeta 
  }
}

class InvoiceService {
  async getInvoices(params?: InvoiceFilters & { page?: number; page_size?: number }): Promise<{ data: Invoice[]; meta: PaginationMeta }> {
    const response = await api.get<PaginatedResponse<Invoice>>('/api/v1/invoices/', { params })
    return mapListResponse(response.data)
  }

  async getInvoice(id: string): Promise<Invoice> {
    const response = await api.get<any>(`/api/v1/invoices/${id}`)
    const d = response.data
    return (d?.data ?? d) as Invoice
  }

  async createInvoice(data: any): Promise<Invoice> {
    const response = await api.post<any>('/api/v1/invoices/', data)
    const d = response.data
    return (d?.data ?? d) as Invoice
  }

  // Payments
  async getInvoicePayments(invoiceId: string): Promise<InvoicePayment[]> {
    const response = await api.get<InvoicePayment[] | ApiResponse<InvoicePayment[]>>(`/api/v1/invoices/${invoiceId}/payments`)
    const data = response.data as any
    return Array.isArray(data) ? data : (data?.data ?? [])
  }

  async recordPayment(data: {
    invoice_id: string
    amount: number
    method?: string
    notes?: string
    date?: string
    transaction_id?: string
    treasury_id?: number | null
    treasury_account_id?: string | null
  }): Promise<InvoicePayment> {
    const { invoice_id, ...rest } = data
    const response = await api.post<InvoicePayment>(`/api/v1/invoices/${invoice_id}/payments`, rest)
    return (response.data as any)?.data ?? response.data
  }

  async submitPayment(
    invoiceId: string,
    paymentId: string,
    data: {
      amount?: number
      method?: string
      notes?: string
      date?: string
      transaction_id?: string
      treasury_id?: number | null
      treasury_account_id?: string | null
    } = {},
  ): Promise<InvoicePayment> {
    const response = await api.post<InvoicePayment>(
      `/api/v1/invoices/${invoiceId}/payments/${paymentId}/submit`,
      data,
    )
    return (response.data as any)?.data ?? response.data
  }

  async updateInvoice(id: string, data: any): Promise<Invoice> {
    const response = await api.put<any>(`/api/v1/invoices/${id}`, data)
    const d = response.data
    return (d?.data ?? d) as Invoice
  }

  async submitInvoice(id: string): Promise<Invoice> {
    const response = await api.post<any>(`/api/v1/invoices/${id}/submit`)
    const d = response.data
    return (d?.data ?? d) as Invoice
  }

  async cancelInvoice(id: string): Promise<Invoice> {
    const response = await api.post<any>(`/api/v1/invoices/${id}/cancel`)
    const d = response.data
    return (d?.data ?? d) as Invoice
  }

  async deleteInvoice(id: string): Promise<void> {
    await api.delete(`/api/v1/invoices/${id}`)
  }

  async uploadPaymentFile(invoiceId: string, paymentId: string, file: File): Promise<InvoicePaymentFile> {
    const formData = new FormData()
    formData.append('file', file)

    const response = await api.post<any>(
      `/api/v1/invoices/${invoiceId}/payments/${paymentId}/files`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    )
    const d = response.data
    return (d?.data ?? d) as InvoicePaymentFile
  }

  async fetchPaymentFileBlob(invoiceId: string, paymentId: string, fileId: string): Promise<Blob> {
    const response = await api.get(
      `/api/v1/invoices/${invoiceId}/payments/${paymentId}/files/${fileId}/download`,
      { responseType: 'blob' },
    )
    return response.data as Blob
  }

  async downloadPaymentFile(
    invoiceId: string,
    paymentId: string,
    fileId: string,
    fileName: string,
  ): Promise<void> {
    const blob = await this.fetchPaymentFileBlob(invoiceId, paymentId, fileId)
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = fileName
    anchor.click()
    URL.revokeObjectURL(url)
  }

  async deletePaymentFile(invoiceId: string, paymentId: string, fileId: string): Promise<void> {
    await api.delete(`/api/v1/invoices/${invoiceId}/payments/${paymentId}/files/${fileId}`)
  }

  async uploadAttachment(invoiceId: string, file: File): Promise<InvoiceAttachment> {
    const formData = new FormData()
    formData.append('file', file)

    const response = await api.post<any>(`/api/v1/invoices/${invoiceId}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    const d = response.data
    return (d?.data ?? d) as InvoiceAttachment
  }

  async fetchAttachmentBlob(invoiceId: string, fileId: string): Promise<Blob> {
    const response = await api.get(`/api/v1/invoices/${invoiceId}/attachments/${fileId}/download`, {
      responseType: 'blob',
    })
    return response.data as Blob
  }

  async deleteAttachment(invoiceId: string, fileId: string): Promise<void> {
    await api.delete(`/api/v1/invoices/${invoiceId}/attachments/${fileId}`)
  }

  async fetchPdfBlob(invoiceId: string): Promise<Blob> {
    const response = await api.get(`/api/v1/invoices/${invoiceId}/pdf`, { responseType: 'blob' })
    return response.data as Blob
  }

  async reprintPdf(invoiceId: string): Promise<Invoice> {
    const response = await api.post<any>(`/api/v1/invoices/${invoiceId}/reprint`)
    const d = response.data
    return (d?.data ?? d) as Invoice
  }
}

export default new InvoiceService()
